/**
 * Date and time formatting, shared by every web screen.
 *
 * This exists because the two platforms must agree on what a date *says*, not
 * just what it means. The mobile app pins its formatting to `en-US`
 * (src/screens/TasksScreen.js, formatDate/formatTime), so "11 Sep" on the phone
 * is "Sep 11" and 21:00 is "9:00 PM". A browser left to its own devices formats
 * with the visitor's locale, so the same task would read "11/09/2026" in London
 * and "9/11/2026" in New York -- two different dates to anyone reading them, off
 * the same stored value.
 *
 * So: one module, locale pinned, every screen calls it. Nothing on the web
 * should call toLocaleDateString directly.
 *
 * The second rule is that a *day* is a local day. `toISOString().slice(0, 10)`
 * is a conversion to UTC, not a date formatter -- at 21:00 in London it returns
 * tomorrow. Day comparisons go through `dayKey` in ./score, which builds the key
 * from local getFullYear/getMonth/getDate. That bug has already shipped three
 * times in this codebase; it does not need to ship a fourth.
 */

/** The locale the mobile app pins. Do not make this dynamic. */
const LOCALE = 'en-US';

const parse = (value: unknown): Date | null => {
  if (!value) return null;
  // Firestore Timestamps arrive with a toDate(); ISO strings and epoch numbers
  // go through the Date constructor.
  const d =
    typeof (value as any)?.toDate === 'function'
      ? (value as any).toDate()
      : new Date(value as any);
  return Number.isNaN(d.getTime()) ? null : d;
};

const atMidnight = (d: Date) => {
  const c = new Date(d);
  c.setHours(0, 0, 0, 0);
  return c;
};

/**
 * A deadline, as the phone writes it: "Today", "Tomorrow", else "Sep 11" --
 * with the year appended only when it is not the current one, which is what
 * mobile's formatDate does.
 */
export const formatDate = (value: unknown): string => {
  const d = parse(value);
  if (!d) return '';

  const today = atMidnight(new Date());
  const tomorrow = new Date(today);
  tomorrow.setDate(tomorrow.getDate() + 1);
  const day = atMidnight(d);

  if (day.getTime() === today.getTime()) return 'Today';
  if (day.getTime() === tomorrow.getTime()) return 'Tomorrow';

  return d.toLocaleDateString(LOCALE, {
    month: 'short',
    day: 'numeric',
    year: d.getFullYear() !== new Date().getFullYear() ? 'numeric' : undefined,
  });
};

/** A time, as the phone writes it: 12-hour with a meridiem -- "9:00 PM". */
export const formatTime = (value: unknown): string => {
  const d = parse(value);
  if (!d) return '';
  return d.toLocaleTimeString(LOCALE, { hour: 'numeric', minute: '2-digit' });
};

/** "Sep 11 at 9:00 PM", the pairing mobile uses in task detail. */
export const formatDateTime = (value: unknown): string => {
  const date = formatDate(value);
  if (!date) return '';
  const time = formatTime(value);
  return time ? `${date} at ${time}` : date;
};

/** Relative age, as mobile formats comment timestamps. */
export const formatRelative = (value: unknown): string => {
  const d = parse(value);
  if (!d) return '';
  const mins = Math.floor((Date.now() - d.getTime()) / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return d.toLocaleDateString(LOCALE, { month: 'short', day: 'numeric' });
};

/* ------------------------------------------------------------------ *
 * Form inputs.
 *
 * <input type="date"> and type="time" are the one safe place to leave
 * formatting alone: the browser shows the visitor's own convention, which is
 * what they expect while typing, but the `value` it exchanges is always
 * ISO -- yyyy-mm-dd and HH:mm. So there is no ambiguity to guard against,
 * provided we read and write that value rather than the displayed text.
 * ------------------------------------------------------------------ */

/** A Date -> the yyyy-mm-dd an <input type="date"> wants, in local time. */
export const toDateInput = (value: unknown): string => {
  const d = parse(value);
  if (!d) return '';
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
};

/** A Date -> the HH:mm an <input type="time"> wants, in local time. */
export const toTimeInput = (value: unknown): string => {
  const d = parse(value);
  if (!d) return '';
  const p = (n: number) => String(n).padStart(2, '0');
  return `${p(d.getHours())}:${p(d.getMinutes())}`;
};

/**
 * The inverse: a date field and an optional time field back into the ISO string
 * the task documents store. Built via the Date constructor's local-time
 * arguments, so the instant is the one the user meant in their own zone.
 */
export const fromDateTimeInputs = (
  dateStr: string,
  timeStr?: string,
): string | null => {
  if (!dateStr) return null;
  const [y, m, d] = dateStr.split('-').map(Number);
  if (!y || !m || !d) return null;
  const [hh, mm] = (timeStr || '').split(':').map(Number);
  const date = new Date(y, m - 1, d, Number.isFinite(hh) ? hh : 0, Number.isFinite(mm) ? mm : 0, 0, 0);
  return Number.isNaN(date.getTime()) ? null : date.toISOString();
};

export { parse as parseDate };

/* ------------------------------------------------------------------ *
 * Longer forms, for screens that name a day or a month outright.
 *
 * The Calendar is the reason these exist. The phone's CalendarScreen calls
 * toLocaleDateString with `undefined` in four separate places -- its month
 * label, its selected-day label, and both of its task formatters -- so that
 * one screen renders four different locale conventions depending on the
 * device. Pinned here like everything else.
 * ------------------------------------------------------------------ */

/** "September 2026" -- a month heading. */
export const formatMonthYear = (value: unknown): string => {
  const d = parse(value);
  return d ? d.toLocaleDateString(LOCALE, { month: 'long', year: 'numeric' }) : '';
};

/** "Friday, September 11" -- a day heading. */
export const formatDayLong = (value: unknown): string => {
  const d = parse(value);
  return d ? d.toLocaleDateString(LOCALE, { weekday: 'long', month: 'long', day: 'numeric' }) : '';
};

/** "Sep 11, 2026" -- an unambiguous absolute date, year always shown. */
export const formatDateFull = (value: unknown): string => {
  const d = parse(value);
  return d ? d.toLocaleDateString(LOCALE, { day: 'numeric', month: 'short', year: 'numeric' }) : '';
};

/** "Sep 11, 9:00 AM" -- a scheduled moment. */
export const formatShortDateTime = (value: unknown): string => {
  const d = parse(value);
  if (!d) return '';
  return `${d.toLocaleDateString(LOCALE, { day: 'numeric', month: 'short' })}, ${formatTime(d)}`;
};
